import { DashboardWindowName, GaragesActivity, OrdersDashboard, ReviewsDashboard, UsersActivity, AuditDashboard } from '../../types/audit';
import BlockErrorState from './BlockErrorState';
import { DashboardBlock } from './useDashboardBlock';

interface ActivityCountersProps {
  window: DashboardWindowName;
  onWindowChange: (w: DashboardWindowName) => void;
  garages: DashboardBlock<GaragesActivity>;
  users: DashboardBlock<UsersActivity>;
  orders: DashboardBlock<OrdersDashboard>;
  reviews: DashboardBlock<ReviewsDashboard>;
  audit: DashboardBlock<AuditDashboard>;
}

interface Counter { id: string; label: string; value: number | undefined }

function Counters({ items }: { items: Counter[] }) {
  return (
    <>
      {items.map((c) => (
        <div key={c.id} className="dash-counter" data-counter={c.id}>
          <span className="dash-counter__value">{c.value ?? 0}</span>
          <span className="dash-counter__label">{c.label}</span>
        </div>
      ))}
    </>
  );
}

function Block<T>({ block, title, render }: { block: DashboardBlock<T>; title: string; render: (d: T) => Counter[] }) {
  if (block.status === 'hidden' || block.status === 'idle') return null;
  if (block.status === 'error') return <BlockErrorState title={title} message={block.error} onRetry={block.retry} />;
  if (block.status === 'loading' || !block.data) {
    return <div className="dash-counter dash-counter--loading" aria-hidden="true"><span className="dash-counter__value">…</span><span className="dash-counter__label">{title}</span></div>;
  }
  return <Counters items={render(block.data)} />;
}

/**
 * Spec 069 AC-A3 — what happened in the window, on Asia/Dubai calendar days. Each source is its own
 * block: one failing shows Retry, the rest still render.
 */
export default function ActivityCounters({ window, onWindowChange, garages, users, orders, reviews, audit }: ActivityCountersProps) {
  return (
    <section className="dash-section" aria-labelledby="activity-title">
      <div className="dash-section__head">
        <h3 id="activity-title" className="dash-section__title">Activity</h3>
        <div className="dash-toggle" role="group" aria-label="Activity window">
          {(['TODAY', 'LAST_7_DAYS'] as DashboardWindowName[]).map((w) => (
            <button
              key={w}
              type="button"
              className={`dash-toggle__btn${window === w ? ' dash-toggle__btn--active' : ''}`}
              aria-pressed={window === w}
              onClick={() => onWindowChange(w)}
            >
              {w === 'TODAY' ? 'Today' : '7 days'}
            </button>
          ))}
        </div>
      </div>
      <div className="dash-counters">
        <Block block={garages} title="Services and garages" render={(d) => [
          { id: 'new-services-partner', label: 'New services by garages', value: d.newServicesPartner },
          { id: 'new-services-admin', label: 'New services by admins', value: d.newServicesAdmin },
          { id: 'garages-submitted', label: 'Garages submitted', value: d.garagesSubmitted },
        ]} />
        <Block block={users} title="Customer sign-ups" render={(d) => [
          { id: 'customer-signups', label: 'Customer sign-ups', value: d.customerSignups },
        ]} />
        <Block block={orders} title="Orders placed" render={(d) => [
          { id: 'orders-placed', label: 'Orders placed', value: d.ordersPlaced },
        ]} />
        <Block block={reviews} title="Reviews posted" render={(d) => [
          { id: 'reviews-posted', label: 'Reviews posted', value: d.reviewsPosted },
        ]} />
        <Block block={audit} title="Admin actions" render={(d) => [
          { id: 'admin-actions', label: 'Admin actions', value: d.adminActions },
        ]} />
      </div>
    </section>
  );
}
